import { GENERATION_TYPE_LABELS, getModelGenerationTypes } from '../../../../../shared-generation-pricing-config'
import { getParamMeta } from '../constants/paramMeta'
import { ModelDetailSection } from './ModelDetailSection'
import type { GenerationModelMetadata, GenerationModelPricing, UnifiedModel } from '../types/unified-model.types'
import type { GenerationType } from '../../../../../shared-generation-pricing-config'
import { formatCost, formatPriceUnit } from '@/shared/utils/generationPricing'
import { DocAccordion } from '@/features/docs/components'
import { getFAQSchema, renderJsonLd } from '@/lib/structured-data'

interface ModelDetailGenerationFAQProps {
  model: UnifiedModel & { metadata: GenerationModelMetadata; pricing: GenerationModelPricing }
  baseModel: string
  genType: GenerationType
}

export function ModelDetailGenerationFAQ({ model, baseModel, genType }: ModelDetailGenerationFAQProps) {
  const otherTypes = getModelGenerationTypes(baseModel).filter((t) => t !== genType)
  const paramLabels = (model.metadata.supportedParams ?? []).map((p) => getParamMeta(p).label).join(', ')
  const price = `${formatCost(model.pricing.priceMillidollars)} ${formatPriceUnit(model.pricing.priceUnit)}`

  const faqs = [
    {
      question: `How is ${model.name} billed on ModelStack?`,
      answer: `${model.name} (${GENERATION_TYPE_LABELS[genType] ?? genType}) is billed at a flat, prepaid rate of ${price} — generation pricing has no tier-based discounts. Final request costs are available in the console.`,
    },
    otherTypes.length > 0
      ? {
          question: `What other generation types does ${model.name} support?`,
          answer: `Besides ${GENERATION_TYPE_LABELS[genType] ?? genType}, ${model.name} also supports: ${otherTypes.map((t) => GENERATION_TYPE_LABELS[t] ?? t).join(', ')}.`,
        }
      : null,
    paramLabels
      ? {
          question: `Which parameters does ${model.name} support?`,
          answer: `${model.name} supports: ${paramLabels}.`,
        }
      : null,
    {
      question: `How can I generate with ${model.name}?`,
      answer: `Use the Generate composer in your dashboard, or call the API directly with model \`${baseModel}\` and type \`${genType}\` — see the code example above.`,
    },
  ].filter((faq): faq is { question: string; answer: string } => faq !== null)

  return (
    <ModelDetailSection title="Frequently asked questions">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: renderJsonLd(getFAQSchema(faqs)) }}
      />
      <div className="flex flex-col gap-2">
        {faqs.map((faq) => (
          <DocAccordion key={faq.question} title={faq.question}>
            {faq.answer}
          </DocAccordion>
        ))}
      </div>
    </ModelDetailSection>
  )
}
